'use client';

import { useCallback, useEffect, useRef } from 'react';
import { SpartanProductFeatureCopyList } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-copy-list';
import { SpartanProductFeatureList } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/feature-list';
import { useFeatureCarousel } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/hooks/useFeatureCarousel';
import { getFeatureImageMotionProgress, getFeatureImageMotionTransform, getFeatureImageSwipeBlend, getFeaturePrimaryImageOpacity } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/image-motion';
import { SpartanProductFeatureThumbnailList } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/thumbnail-list';
import type { SpartanProductFeatureThumbnailListHandle } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/thumbnail-list/types';
import { getSpartanProductFeatureCarouselTracking } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/tracking';
import type { SpartanProductFeatureCarouselProps } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/types';
import { useSpartanProductTopNavigationMediaPause } from '@/app/lcv/components/sections/spartan/products/shared/hooks/useTopNavigationMediaPause';
import { getSpartanVideoVisibilityThreshold } from '@/app/lcv/components/sections/spartan/products/shared/in-view-video/constants';
import type { SpartanInViewVideoHandle } from '@/app/lcv/components/sections/spartan/products/shared/in-view-video/types';
import { normalizeMediaProgress } from '@/app/lcv/components/sections/spartan/shared/hooks/mediaProgress';
const IMAGE_RESET_CLEARANCE_PX = 2;
export const SpartanProductFeatureCarousel = ({
  features,
  trackingPrefix,
  dataGASection,
  paused = false,
  tone = 'dark',
  loop = true,
  imageDurationMs,
  videoStartMode = 'visible',
  videoVisibilityThreshold,
  progressMode = 'deplete',
  clipThumbnailViewport = true,
  motionStyle
}: SpartanProductFeatureCarouselProps) => {
  const tracking = getSpartanProductFeatureCarouselTracking(trackingPrefix);
  const isTopNavigationMediaPaused = useSpartanProductTopNavigationMediaPause();
  const resolvedVideoVisibilityThreshold = videoVisibilityThreshold ?? getSpartanVideoVisibilityThreshold(videoStartMode);
  const videoControlRefs = useRef<Array<SpartanInViewVideoHandle | null>>([]);
  const thumbnailListRef = useRef<SpartanProductFeatureThumbnailListHandle | null>(null);
  const imageMotionRefs = useRef<Array<HTMLImageElement | null>>([]);
  const secondaryImageMotionRefs = useRef<Array<HTMLImageElement | null>>([]);
  const primaryLayerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const secondaryImageReadyRefs = useRef<boolean[]>([]);
  const imageProgressRef = useRef<number[]>([]);
  const imageMotionProgressRef = useRef<number[]>([]);
  const imageMotionStartRef = useRef<Array<{
    playback: number;
    motion: number;
  } | undefined>>([]);
  const imageSwipeStartRef = useRef<Array<{
    playback: number;
    motion: number;
  } | undefined>>([]);
  const imageSwipeDistanceRef = useRef<number[]>([]);
  const imageDirectionRef = useRef<Array<-1 | 1>>([]);
  const activeSwipeIndexRef = useRef<number>(0);
  const isImageMotionSettledRef = useRef<boolean>(true);
  const applyImageTransform = useCallback((index: number, transform: string) => {
    const primary = imageMotionRefs.current[index];
    if (primary) primary.style.transform = transform;
    const secondary = secondaryImageMotionRefs.current[index];
    if (secondary) secondary.style.transform = transform;
  }, []);
  const updateCrossfade = useCallback((index_0: number, progress: number) => {
    const layer = primaryLayerRefs.current[index_0];
    if (!layer) return;
    layer.style.opacity = secondaryImageReadyRefs.current[index_0] ? String(getFeaturePrimaryImageOpacity(progress)) : '1';
  }, []);
  const setPrimaryLayerRef = useCallback((index_1: number, layer_0: HTMLDivElement | null) => {
    primaryLayerRefs.current[index_1] = layer_0;
    updateCrossfade(index_1, imageProgressRef.current[index_1] ?? 0);
  }, [updateCrossfade]);
  const handleSecondaryImageSettled = useCallback((index_2: number, loaded: boolean) => {
    secondaryImageReadyRefs.current[index_2] = loaded;
    updateCrossfade(index_2, imageProgressRef.current[index_2] ?? 0);
  }, [updateCrossfade]);
  const applySwipeMotion = useCallback((index_3: number, distance: number) => {
    const start = imageSwipeStartRef.current[index_3];
    if (!start) return;
    const target = getFeatureImageMotionProgress(imageProgressRef.current[index_3] ?? 0, start.playback, start.motion);
    const blend = getFeatureImageSwipeBlend(distance);
    const motion = start.motion + (target - start.motion) * blend;
    imageMotionProgressRef.current[index_3] = motion;
    if (imageMotionRefs.current[index_3]) {
      applyImageTransform(index_3, getFeatureImageMotionTransform(motion, imageDirectionRef.current[index_3] ?? -1));
    }
  }, [applyImageTransform]);
  const handleImageSwipeProgress = useCallback((index_4: number, distance_0: number) => {
    if (isImageMotionSettledRef.current || index_4 !== activeSwipeIndexRef.current) {
      return;
    }
    imageSwipeDistanceRef.current[index_4] = distance_0;
    applySwipeMotion(index_4, distance_0);
  }, [applySwipeMotion]);
  const updateProgressVisual = useCallback((index_5: number, value: number) => {
    thumbnailListRef.current?.updateProgress(index_5, value);
  }, []);
  const updateImageProgress = useCallback((index_6: number, value_0: number) => {
    updateProgressVisual(index_6, value_0);
    imageProgressRef.current[index_6] = value_0;
    updateCrossfade(index_6, value_0);
    if (value_0 === 0) {
      imageMotionStartRef.current[index_6] = undefined;
      imageSwipeStartRef.current[index_6] = undefined;
      imageMotionProgressRef.current[index_6] = 0;
    }
    if (!isImageMotionSettledRef.current) {
      if (index_6 === activeSwipeIndexRef.current) {
        applySwipeMotion(index_6, imageSwipeDistanceRef.current[index_6] ?? 1);
      }
      return;
    }
    const start_0 = imageMotionStartRef.current[index_6];
    const motion_0 = getFeatureImageMotionProgress(value_0, start_0?.playback ?? 0, start_0?.motion ?? 0);
    imageMotionProgressRef.current[index_6] = motion_0;
    if (imageMotionRefs.current[index_6]) {
      applyImageTransform(index_6, getFeatureImageMotionTransform(motion_0, imageDirectionRef.current[index_6] ?? -1));
    }
  }, [applyImageTransform, applySwipeMotion, updateProgressVisual, updateCrossfade]);
  const setImageMotionRef = useCallback((index_7: number, image: HTMLImageElement | null, secondary_0 = false) => {
    if (secondary_0) secondaryImageMotionRefs.current[index_7] = image;else imageMotionRefs.current[index_7] = image;
    if (image) {
      if (features.length <= 1) {
        image.style.removeProperty('transform');
        return;
      }
      image.style.transform = getFeatureImageMotionTransform(imageMotionProgressRef.current[index_7] ?? 0, imageDirectionRef.current[index_7] ?? -1);
    }
  }, [features.length]);
  const {
    wrapperRef,
    emblaApi,
    emblaRef,
    thumbnailEmblaRef,
    thumbnailOverflows,
    selectedIndex,
    isNearViewport,
    isPlaybackEligible,
    canSelectFeature,
    canSelectPrevious,
    canSelectNext,
    selectSlide,
    selectPreviousSlide,
    selectNextSlide,
    cancelAutoAdvance,
    handleVideoEnded,
    handleImageSettled,
    isImagePlaybackActive,
    prefersReducedMotion,
    isVideoPlaybackActive,
    isVideoStarting,
    isVideoExternallyPaused,
    shouldPreloadVideo,
    pausePlayback,
    resumePlayback,
    handleVideoPlaying,
    handleVideoPause,
    handleVideoWaiting,
    handleVideoSeeking,
    handleVideoSeeked,
    handleVideoError
  } = useFeatureCarousel(features, {
    isExternallyPaused: paused || isTopNavigationMediaPaused,
    imageDurationMs,
    loop,
    visibilityThreshold: resolvedVideoVisibilityThreshold,
    onImageProgress: updateImageProgress
  });
  const isParallaxEnabled = motionStyle === 'parallax' && !prefersReducedMotion;
  useEffect(() => {
    if (!emblaApi) return;
    let hiddenResetFrame: number | null = null;
    const resetHiddenImages = () => {
      const selectedIndex_0 = emblaApi.selectedScrollSnap();
      const slidesInView = new Set(emblaApi.slidesInView());
      let viewport: DOMRect | undefined;
      let slides: HTMLElement[] | undefined;
      imageMotionRefs.current.forEach((image_0, index_8) => {
        if (!image_0 || index_8 === selectedIndex_0 || slidesInView.has(index_8)) {
          return;
        }
        if (imageProgressRef.current[index_8] === 0 && imageMotionProgressRef.current[index_8] === 0 && !imageMotionStartRef.current[index_8] && !imageSwipeStartRef.current[index_8] && image_0.style.transform === getFeatureImageMotionTransform(0, imageDirectionRef.current[index_8] ?? -1)) {
          return;
        }
        viewport ??= emblaApi.rootNode().getBoundingClientRect();
        slides ??= emblaApi.slideNodes();
        const slide = slides[index_8]?.getBoundingClientRect();
        if (!slide || slide.right > viewport.left - IMAGE_RESET_CLEARANCE_PX && slide.left < viewport.right + IMAGE_RESET_CLEARANCE_PX) {
          return;
        }
        imageProgressRef.current[index_8] = 0;
        imageMotionProgressRef.current[index_8] = 0;
        imageMotionStartRef.current[index_8] = undefined;
        imageSwipeStartRef.current[index_8] = undefined;
        imageSwipeDistanceRef.current[index_8] = 0;
        applyImageTransform(index_8, getFeatureImageMotionTransform(0, imageDirectionRef.current[index_8] ?? -1));
        updateCrossfade(index_8, 0);
      });
    };
    const scheduleHiddenReset = () => {
      if (hiddenResetFrame !== null) return;
      hiddenResetFrame = window.requestAnimationFrame(() => {
        hiddenResetFrame = null;
        resetHiddenImages();
      });
    };
    const cancelHiddenReset = () => {
      if (hiddenResetFrame === null) return;
      window.cancelAnimationFrame(hiddenResetFrame);
      hiddenResetFrame = null;
    };
    const setImageDirection = (index_9: number, direction: -1 | 1) => {
      if (imageDirectionRef.current[index_9] === direction) return;
      imageDirectionRef.current[index_9] = direction;
      if (imageMotionRefs.current[index_9]) {
        applyImageTransform(index_9, getFeatureImageMotionTransform(imageMotionProgressRef.current[index_9] ?? 0, direction));
      }
    };
    const getTravel = () => {
      const {
        location,
        target: target_0,
        scrollBody
      } = emblaApi.internalEngine();
      return Math.sign(target_0.get() - location.get()) || scrollBody.direction();
    };
    let selectedDuringSwipe = false;
    const beginSwipe = (index_10: number) => {
      isImageMotionSettledRef.current = false;
      activeSwipeIndexRef.current = index_10;
      if (!imageSwipeStartRef.current[index_10]) {
        imageSwipeStartRef.current[index_10] = {
          playback: imageProgressRef.current[index_10] ?? 0,
          motion: imageMotionProgressRef.current[index_10] ?? 0
        };
        imageSwipeDistanceRef.current[index_10] = 1;
      }
    };
    const handleSelect = () => {
      selectedDuringSwipe = true;
      const index_11 = emblaApi.selectedScrollSnap();
      beginSwipe(index_11);
      const travel = getTravel();
      if (travel === 0) return;
      setImageDirection(index_11, travel < 0 ? -1 : 1);
    };
    const handleScroll = () => {
      cancelHiddenReset();
      const index_12 = emblaApi.selectedScrollSnap();
      if (isParallaxEnabled && features.length > 1 && !selectedDuringSwipe) {
        const travel_0 = getTravel();
        if (travel_0 !== 0) {
          const next = (index_12 + (travel_0 < 0 ? 1 : -1) + features.length) % features.length;
          setImageDirection(next, travel_0 < 0 ? -1 : 1);
        }
      }
      beginSwipe(index_12);
    };
    const handleSettle = () => {
      selectedDuringSwipe = false;
      const selectedIndex_1 = emblaApi.selectedScrollSnap();
      if (isParallaxEnabled) applySwipeMotion(selectedIndex_1, 0);
      isImageMotionSettledRef.current = true;
      const motion_1 = imageMotionProgressRef.current[selectedIndex_1] ?? 0;
      // Continue from the blended swipe position without resetting playback.
      imageMotionStartRef.current[selectedIndex_1] = {
        playback: imageProgressRef.current[selectedIndex_1] ?? 0,
        motion: motion_1
      };
      const image_1 = imageMotionRefs.current[selectedIndex_1];
      if (image_1) {
        const transform_0 = getFeatureImageMotionTransform(motion_1, imageDirectionRef.current[selectedIndex_1] ?? -1);
        if (image_1.style.transform !== transform_0) applyImageTransform(selectedIndex_1, transform_0);
      }
      imageSwipeStartRef.current[selectedIndex_1] = undefined;
      imageSwipeDistanceRef.current[selectedIndex_1] = 0;
      scheduleHiddenReset();
    };
    emblaApi.on('select', handleSelect);
    emblaApi.on('scroll', handleScroll);
    emblaApi.on('settle', handleSettle);
    return () => {
      cancelHiddenReset();
      emblaApi.off('select', handleSelect);
      emblaApi.off('scroll', handleScroll);
      emblaApi.off('settle', handleSettle);
    };
  }, [applyImageTransform, applySwipeMotion, emblaApi, features.length, isParallaxEnabled, updateCrossfade]);
  const handleVideoProgress = useCallback((index_13: number, currentTime: number, duration: number) => {
    updateProgressVisual(index_13, normalizeMediaProgress(currentTime, duration));
  }, [updateProgressVisual]);
  const handleToggleActivePlayback = useCallback(() => {
    if (!isPlaybackEligible) return;
    const activeFeature = features[selectedIndex];
    if (!activeFeature) return;
    if (activeFeature.video?.desktop.src) {
      if (isVideoPlaybackActive || isVideoStarting) {
        pausePlayback();
        videoControlRefs.current[selectedIndex]?.pause();
        return;
      }
      resumePlayback();
      videoControlRefs.current[selectedIndex]?.play();
      return;
    }
    if (isImagePlaybackActive) {
      pausePlayback();
      return;
    }
    resumePlayback();
  }, [features, isImagePlaybackActive, isPlaybackEligible, isVideoPlaybackActive, isVideoStarting, pausePlayback, resumePlayback, selectedIndex]);
  if (!features.length) return null;
  return <div ref={wrapperRef} className="relative">
      <SpartanProductFeatureList features={features} tracking={tracking} dataGASection={dataGASection} selectedIndex={selectedIndex} emblaApi={emblaApi} emblaRef={emblaRef} videoControlRefs={videoControlRefs} onImageMotionRef={setImageMotionRef} onPrimaryLayerRef={setPrimaryLayerRef} onSecondaryImageSettled={handleSecondaryImageSettled} onImageSwipeProgress={handleImageSwipeProgress} isParallaxEnabled={isParallaxEnabled} isNearViewport={isNearViewport} canSelectPrevious={canSelectPrevious} canSelectNext={canSelectNext} isVideoExternallyPaused={isVideoExternallyPaused} videoStartMode={videoStartMode} videoVisibilityThreshold={resolvedVideoVisibilityThreshold} onCancelAutoAdvance={cancelAutoAdvance} onVideoProgress={handleVideoProgress} onVideoPlaying={handleVideoPlaying} onVideoPause={handleVideoPause} onVideoWaiting={handleVideoWaiting} onVideoSeeking={handleVideoSeeking} onVideoSeeked={handleVideoSeeked} onVideoError={handleVideoError} onVideoEnded={handleVideoEnded} onImageSettled={handleImageSettled} shouldPreloadVideo={shouldPreloadVideo} />

      {features.length > 1 && <SpartanProductFeatureThumbnailList ref={thumbnailListRef} features={features} tracking={tracking} dataGASection={dataGASection} selectedIndex={selectedIndex} emblaRef={thumbnailEmblaRef} thumbnailOverflows={thumbnailOverflows} clipViewport={clipThumbnailViewport} isPlaybackEligible={isPlaybackEligible} isImagePlaybackActive={isImagePlaybackActive} isVideoPlaybackActive={isVideoPlaybackActive} isVideoStarting={isVideoStarting} progressMode={progressMode} canSelectFeature={canSelectFeature} onSelectSlide={selectSlide} onTogglePlayback={handleToggleActivePlayback} />}

      <SpartanProductFeatureCopyList features={features} tracking={tracking} selectedIndex={selectedIndex} tone={tone} onSelectPrevious={selectPreviousSlide} onSelectNext={selectNextSlide} />
    </div>;
};